// Google Workspace integration layer (Phase 5). ALL Google API traffic goes
// through this module — it is the stub seam for tests and the lift-and-shift
// seam if sync ever moves server-side (Blaze).
//
// Client-side only (Spark plan): access tokens are acquired in the browser via
// Google Identity Services (GIS) and live in module memory — never persisted.
// localStorage holds only a boolean "has connected" flag so surfaces can
// attempt silent token re-acquisition instead of showing a Connect prompt.
// Deliberately Firebase-free: callers compose Firestore writes themselves.

export const GOOGLE_SCOPES = [
  'https://www.googleapis.com/auth/calendar.events',
  'https://www.googleapis.com/auth/drive.readonly',
  'https://www.googleapis.com/auth/gmail.readonly',
  'https://www.googleapis.com/auth/gmail.send',
  'https://www.googleapis.com/auth/directory.readonly',
]

// Same for every Career Nomad workspace user — safe to hardcode (public
// identifiers, not secrets). Env vars still win, so a different environment
// (e.g. a fork, or a future multi-tenant setup) can override without a code
// change. A real Settings UI for these is planned — see
// docs/superpowers/plans/2026-07-06-google-settings-page.md.
const DEFAULT_CLIENT_ID = '1058347796010-k3fr4v2sdi5grmohmv0l7h9ef5d1h614.apps.googleusercontent.com'

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || DEFAULT_CLIENT_ID
export const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_API_KEY || ''
const CONNECTED_FLAG = 'caravan.googleConnected'

// ── connection state: immutable snapshot + subscribers (useSyncExternalStore) ─
let state = { status: 'disconnected', error: null }
const listeners = new Set()
function setState(patch) {
  state = { ...state, ...patch }
  listeners.forEach((fn) => fn())
}
export function getGoogleState() { return state }
export function subscribeGoogle(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

let accessToken = null
let tokenExpiresAt = 0

// Cache the access token in localStorage so a page refresh — or reopening the
// app in a new tab — REUSES it instead of re-running the GIS flow (and its
// popup). It holds only a short-lived (~1h) access token, never a refresh token,
// and is removed on disconnect and once expired. Any web-storage token is
// XSS-readable, so exposure is bounded to ≤1h of the granted scopes on this
// internal app. Node/SSR: localStorage is absent, so all helpers are safe no-ops.
const TOKEN_KEY = 'caravan.googleToken'
function persistToken() {
  try {
    if (typeof localStorage !== 'undefined')
      localStorage.setItem(TOKEN_KEY, JSON.stringify({ accessToken, tokenExpiresAt }))
  } catch { /* private mode */ }
}
function clearPersistedToken() {
  try { if (typeof localStorage !== 'undefined') localStorage.removeItem(TOKEN_KEY) } catch { /* private mode */ }
}
// Rehydrate a still-valid token from a prior load so a refresh is popup-free.
// Runs at import (before auth.jsx's post-login connect) so the connected state
// is already set and the auto-connect guard skips re-acquiring.
try {
  if (typeof localStorage !== 'undefined') {
    const saved = JSON.parse(localStorage.getItem(TOKEN_KEY) || 'null')
    if (saved && saved.accessToken && Date.now() < saved.tokenExpiresAt - 60_000) {
      accessToken = saved.accessToken
      tokenExpiresAt = saved.tokenExpiresAt
      state = { status: 'connected', error: null }
    } else if (saved) {
      localStorage.removeItem(TOKEN_KEY)
    }
  }
} catch { /* ignore malformed cache */ }

export function hasConnectedBefore() {
  try { return localStorage.getItem(CONNECTED_FLAG) === '1' } catch { return false }
}

// ── GIS loader: inject the script once, on first use (never at import time) ──
let gisPromise = null
function loadGis() {
  if (gisPromise) return gisPromise
  gisPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') { gisPromise = null; return reject(new Error('Google integration is browser-only')) }
    if (window.google?.accounts?.oauth2) return resolve(window.google.accounts.oauth2)
    const s = document.createElement('script')
    s.src = 'https://accounts.google.com/gsi/client'
    s.async = true
    s.onload = () => resolve(window.google.accounts.oauth2)
    s.onerror = () => { gisPromise = null; reject(new Error('Failed to load Google Identity Services')) }
    document.head.appendChild(s)
  })
  return gisPromise
}

// GIS token clients deliver via callback; wrap one request in a promise.
// prompt '' lets Google decide: consent screen on first grant, silent after.
async function requestToken({ prompt, hint }) {
  const oauth2 = await loadGis()
  if (!CLIENT_ID) throw new Error('VITE_GOOGLE_CLIENT_ID is not configured — see docs/GOOGLE_SETUP.md')
  return new Promise((resolve, reject) => {
    const client = oauth2.initTokenClient({
      client_id: CLIENT_ID,
      scope: GOOGLE_SCOPES.join(' '),
      callback: (resp) => {
        if (resp.error) return reject(new Error(resp.error_description || resp.error))
        accessToken = resp.access_token
        tokenExpiresAt = Date.now() + (Number(resp.expires_in) || 3600) * 1000
        persistToken()
        resolve(accessToken)
      },
      error_callback: (err) => reject(new Error(err?.message || 'Google authorization failed')),
    })
    client.requestAccessToken({ prompt, ...(hint ? { hint } : {}) })
  })
}

/** Interactive connect — call from a user click so the popup isn't blocked. */
export async function connectGoogle({ hint } = {}) {
  setState({ status: 'connecting', error: null })
  try {
    await requestToken({ prompt: '', hint })
    try { localStorage.setItem(CONNECTED_FLAG, '1') } catch { /* private mode */ }
    setState({ status: 'connected', error: null })
    return true
  } catch (e) {
    setState({ status: 'disconnected', error: e.message })
    throw e
  }
}

/**
 * Return a valid access token, re-acquiring silently when expired. Throws when
 * the user never connected (unless interactive:true, which may show a popup).
 */
export async function ensureToken({ interactive = false, hint } = {}) {
  if (accessToken && Date.now() < tokenExpiresAt - 60_000) return accessToken
  if (!interactive && !hasConnectedBefore()) throw new Error('Google not connected')
  try {
    const token = await requestToken({ prompt: '', hint })
    setState({ status: 'connected', error: null })
    return token
  } catch (e) {
    // Silent re-acquire failed (revoked grant, blocked popup): reflect reality
    // so surfaces fall back to the Connect affordance instead of lying.
    setState({ status: 'disconnected', error: e.message })
    throw e
  }
}

export async function disconnectGoogle() {
  const token = accessToken
  accessToken = null
  tokenExpiresAt = 0
  clearPersistedToken()
  try { localStorage.removeItem(CONNECTED_FLAG) } catch { /* private mode */ }
  setState({ status: 'disconnected', error: null })
  if (token) {
    try { const oauth2 = await loadGis(); oauth2.revoke(token, () => {}) } catch { /* best-effort */ }
  }
}

// ── REST helper ───────────────────────────────────────────────────────────────
export class GoogleApiError extends Error {
  constructor(status, message) {
    super(message)
    this.name = 'GoogleApiError'
    this.status = status
  }
}

/** Authenticated fetch against a Google REST endpoint; one silent 401 retry. */
export async function googleFetch(url, { method = 'GET', body } = {}) {
  // JSON-only body encoding is sufficient for every current surface — Gmail
  // send posts JSON {raw: <base64url RFC822>} through this path (Increment 4).
  const doFetch = (token) => fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  })
  let resp = await doFetch(await ensureToken())
  if (resp.status === 401) {
    accessToken = null // stale token — force a silent re-acquire, then retry once
    resp = await doFetch(await ensureToken())
  }
  if (!resp.ok) {
    const text = await resp.text().catch(() => '')
    throw new GoogleApiError(resp.status, `Google API ${resp.status}: ${text.slice(0, 200)}`)
  }
  return resp.status === 204 ? null : resp.json()
}

// ── People API: own Workspace Directory profile ───────────────────────────────

// `sources` accepts ReadSourceType values only. DOMAIN_PROFILE is a Source.type
// — what appears in organizations[].metadata.source.type below — and passing it
// here made People reject the request with a 400 on every login. Reading the
// PROFILE source already merges the admin-set Workspace domain profile in.
export const SELF_PROFILE_URL = 'https://people.googleapis.com/v1/people/me'
  + '?personFields=photos,organizations,phoneNumbers'
  + '&sources=READ_SOURCE_TYPE_PROFILE'

/** Fetch the signed-in Google user's own profile (photo, org title, phone). */
export function fetchSelfProfile() {
  return googleFetch(SELF_PROFILE_URL)
}

/**
 * Pure: People API person → users-doc patch. Only includes fields that are
 * actually present (never writes null/undefined over existing profile data).
 * Prefers the admin-set DOMAIN_PROFILE org title over a self-set one.
 */
export function mapDirectoryProfile(person) {
  const out = {}
  const photo = (person?.photos || []).find((p) => p.url && !p.default)
  if (photo) out.photoURL = photo.url
  const orgs = person?.organizations || []
  const org = orgs.find((o) => o.title && o.metadata?.source?.type === 'DOMAIN_PROFILE')
    || orgs.find((o) => o.title)
  if (org) out.role = org.title
  const phone = (person?.phoneNumbers || []).find((p) => p.value)
  if (phone) out.phone = phone.value
  return out
}

/**
 * Best-effort Directory refresh: fetch own profile, map it, and hand the
 * non-empty patch to the caller's writer (e.g. auth.updateProfile). Returns
 * the patch so callers can reflect it in local state.
 */
export async function refreshDirectoryProfile(applyPatch, { interactive = false, hint } = {}) {
  await ensureToken({ interactive, hint })
  const person = await fetchSelfProfile()
  const patch = mapDirectoryProfile(person)
  if (Object.keys(patch).length > 0) await applyPatch(patch)
  return patch
}

// ── Drive Picker ──────────────────────────────────────────────────────────────

// Load the Picker library once, on first use (api.js → gapi.load('picker')).
let pickerPromise = null
function loadPickerApi() {
  if (pickerPromise) return pickerPromise
  pickerPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') { pickerPromise = null; return reject(new Error('Google Picker is browser-only')) }
    if (window.google?.picker) return resolve(window.google.picker)
    const s = document.createElement('script')
    s.src = 'https://apis.google.com/js/api.js'
    s.async = true
    // gapi.load can itself fail or hang (flaky module fetch) — without the
    // onerror/timeout handlers a pending pickerPromise would be cached forever
    // and wedge every Drive-file button until reload.
    s.onload = () => window.gapi.load('picker', {
      callback: () => resolve(window.google.picker),
      onerror: () => { pickerPromise = null; reject(new Error('Failed to load Google Picker')) },
      timeout: 10000,
      ontimeout: () => { pickerPromise = null; reject(new Error('Google Picker timed out — try again')) },
    })
    s.onerror = () => { pickerPromise = null; reject(new Error('Failed to load Google Picker')) }
    document.head.appendChild(s)
  })
  return pickerPromise
}

/**
 * Pure: Picker response documents → driveLinks entries
 * ({fileId, name, url, mimeType}). Drops docs without an id or any url.
 */
export function mapPickerDocs(docs) {
  return (docs || [])
    .filter((d) => d && d.id && (d.url || d.embedUrl))
    .map((d) => ({
      fileId: d.id,
      name: d.name || d.id,
      url: d.url || d.embedUrl,
      mimeType: d.mimeType || null,
    }))
}

/**
 * Show the Drive Picker to attach existing files. With drive.readonly the Picker
 * browses the user's files like Gmail's attach dialog. Each addView() adds a tab:
 * My Drive, Shared drives, and Shared with me. Resolves to mapped driveLinks
 * entries — [] on cancel.
 */
export async function driveShowPicker() {
  const token = await ensureToken()
  if (!GOOGLE_API_KEY) throw new Error('VITE_GOOGLE_API_KEY is not configured — see docs/GOOGLE_SETUP.md')
  const picker = await loadPickerApi()
  return new Promise((resolve) => {
    // My Drive tab: the user's own files/folders.
    const myDrive = new picker.DocsView(picker.ViewId.DOCS)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(false) // attach files, not folders
    // Shared drives tab: company/team shared drives (needs SUPPORT_DRIVES below).
    const sharedDrives = new picker.DocsView(picker.ViewId.DOCS)
      .setEnableDrives(true)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(false)
    // Shared with me tab: files others shared with the user (not owned by them).
    const sharedWithMe = new picker.DocsView(picker.ViewId.DOCS)
      .setOwnedByMe(false)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(false)
    new picker.PickerBuilder()
      .enableFeature(picker.Feature.MULTISELECT_ENABLED)
      .enableFeature(picker.Feature.SUPPORT_DRIVES) // required for the Shared drives view
      .addView(myDrive)
      .addView(sharedDrives)
      .addView(sharedWithMe)
      .setOAuthToken(token)
      .setDeveloperKey(GOOGLE_API_KEY)
      .setCallback((data) => {
        const action = data[picker.Response.ACTION]
        if (action === picker.Action.PICKED) resolve(mapPickerDocs(data[picker.Response.DOCUMENTS]))
        else if (action === picker.Action.CANCEL) resolve([])
      })
      .build()
      .setVisible(true)
  })
}

// ── Calendar (shared hub calendar sync) ───────────────────────────────────────

const CAL_BASE = 'https://www.googleapis.com/calendar/v3/calendars'

// Local-time YYYY-MM-DD (date inputs store local midnight; match CalendarView's
// local reads). Tests use midday-UTC fixtures so this can't cross a boundary.
function ymd(d) {
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}
function addDays(d, n) {
  const out = new Date(d)
  out.setDate(out.getDate() + n)
  return out
}
function tsToDateAny(v) {
  if (!v) return null
  return typeof v.toDate === 'function' ? v.toDate() : new Date(v)
}

/** Pure: engagement doc → all-day Calendar event body (null when no date). */
export function buildEngagementEvent(eng) {
  const start = tsToDateAny(eng?.date)
  if (!start) return null
  const end = tsToDateAny(eng.endDate) || start
  return {
    summary: eng.org ? `${eng.title} — ${eng.org}` : (eng.title || 'Engagement'),
    ...(eng.location ? { location: eng.location } : {}),
    description: 'Synced from Caravan (Engagements)',
    start: { date: ymd(start) },
    end: { date: ymd(addDays(end, 1)) }, // Calendar all-day end is exclusive
  }
}

/** Pure: teamDates OOO doc → all-day Calendar event body (null when no start). */
export function buildTeamDateEvent(d) {
  const start = tsToDateAny(d?.start)
  if (!start) return null
  const end = tsToDateAny(d.end) || start
  return {
    summary: [d.who, d.label].filter(Boolean).join(' — ') || 'Team date',
    ...(d.note ? { description: d.note } : {}),
    start: { date: ymd(start) },
    end: { date: ymd(addDays(end, 1)) },
  }
}

/**
 * Pure: sync status for a row. 'na' = nothing to sync (no date), 'unsynced' =
 * has a date but no event yet, 'stale' = edited after the last stamp,
 * 'synced' = stamp is current. The stamp write never touches updatedAt, so
 * updatedAt > calendarSyncedAt is exactly "edited since last sync".
 */
export function calendarSyncState(docData) {
  const ms = (v) => (v && (v.toMillis ? v.toMillis() : new Date(v).getTime())) || 0
  const hasDate = !!(docData?.date || docData?.start)
  if (!hasDate) return 'na'
  if (!docData.calendarEventId) return 'unsynced'
  const synced = ms(docData.calendarSyncedAt)
  if (!synced || ms(docData.updatedAt) > synced) return 'stale'
  return 'synced'
}

/** Pure: Calendar API event items → card entries; drops items without a start. */
export function mapCalendarEvents(items) {
  return (items || [])
    .filter((e) => e && e.id && (e.start?.date || e.start?.dateTime))
    .map((e) => ({
      id: e.id,
      summary: e.summary || '(untitled)',
      start: e.start.date || e.start.dateTime,
      htmlLink: e.htmlLink || null,
    }))
}

/**
 * Create or update the event: PATCH when an eventId is known, INSERT when not
 * — and re-INSERT when the known event was deleted out from under us (404/410).
 * Returns the (possibly new) event id.
 */
export async function calendarUpsertEvent(calendarId, eventId, body) {
  const cal = encodeURIComponent(calendarId)
  if (eventId) {
    try {
      const patched = await googleFetch(`${CAL_BASE}/${cal}/events/${encodeURIComponent(eventId)}`, { method: 'PATCH', body })
      return patched.id
    } catch (e) {
      if (!(e instanceof GoogleApiError) || (e.status !== 404 && e.status !== 410)) throw e
      // event vanished on the calendar side — fall through to a fresh insert
    }
  }
  const created = await googleFetch(`${CAL_BASE}/${cal}/events`, { method: 'POST', body })
  return created.id
}

/** Best-effort delete; an already-gone event (404/410) is success. */
export async function calendarDeleteEvent(calendarId, eventId) {
  try {
    await googleFetch(`${CAL_BASE}/${encodeURIComponent(calendarId)}/events/${encodeURIComponent(eventId)}`, { method: 'DELETE' })
  } catch (e) {
    if (e instanceof GoogleApiError && (e.status === 404 || e.status === 410)) return
    throw e
  }
}

/** Next upcoming events on the shared calendar, soonest first. */
export async function calendarListUpcoming(calendarId, { maxResults = 10 } = {}) {
  const params = new URLSearchParams({
    timeMin: new Date().toISOString(),
    singleEvents: 'true',
    orderBy: 'startTime',
    maxResults: String(maxResults),
  })
  const resp = await googleFetch(`${CAL_BASE}/${encodeURIComponent(calendarId)}/events?${params}`)
  return mapCalendarEvents(resp.items)
}

// ── Gmail (contact email panel + compose) ─────────────────────────────────────

const GMAIL_BASE = 'https://gmail.googleapis.com/gmail/v1/users/me'

// UTF-8-safe base64 (browser btoa is latin-1-only; node lacks btoa in older LTS).
function b64utf8(str) {
  if (typeof TextEncoder !== 'undefined' && typeof btoa !== 'undefined') {
    const bytes = new TextEncoder().encode(str)
    let bin = ''
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
    return btoa(bin)
  }
  return Buffer.from(str, 'utf8').toString('base64')
}

/** Pure: UTF-8 string → URL-safe unpadded base64 (Gmail `raw` format). */
export function toBase64Url(str) {
  return b64utf8(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * Pure: an address list (array, or free text split on `,`/`;`) → trimmed,
 * non-empty, case-insensitively deduped addresses. CR/LF is flattened so an
 * address can never break out of its header line.
 */
export function normalizeAddressList(list) {
  const raw = Array.isArray(list) ? list : String(list || '').split(/[,;]/)
  const out = []
  const seen = new Set()
  raw.forEach((a) => {
    const v = String(a || '').replace(/[\r\n]+/g, ' ').trim()
    if (!v || seen.has(v.toLowerCase())) return
    seen.add(v.toLowerCase())
    out.push(v)
  })
  return out
}

/**
 * Pure: plain-text email → RFC822 message. Non-ASCII subjects use an RFC-2047
 * encoded-word; the body ships as base64 CTE so UTF-8 survives verbatim.
 * Optional `cc` / `bcc` (array, or comma/semicolon text) add headers only when
 * non-empty. Gmail's send API delivers to the Bcc header's addresses and strips
 * it from the copy other recipients see.
 */
export function buildMimeMessage({ to, cc, bcc, subject, body }) {
  const subj = /^[\x20-\x7E]*$/.test(subject) ? subject : `=?UTF-8?B?${b64utf8(subject)}?=`
  const ccList = normalizeAddressList(cc)
  const bccList = normalizeAddressList(bcc)
  return [
    // CR/LF stripped: a line break in the recipient would inject MIME headers.
    // (The subject path is already safe — non-ASCII, incl. CR/LF, is
    // RFC-2047-encoded above; cc/bcc are flattened by normalizeAddressList.)
    `To: ${String(to).replace(/[\r\n]+/g, ' ')}`,
    ...(ccList.length ? [`Cc: ${ccList.join(', ')}`] : []),
    ...(bccList.length ? [`Bcc: ${bccList.join(', ')}`] : []),
    `Subject: ${subj}`,
    'MIME-Version: 1.0',
    'Content-Type: text/plain; charset="UTF-8"',
    'Content-Transfer-Encoding: base64',
    '',
    b64utf8(body),
  ].join('\r\n')
}

/** Pure: Gmail thread (metadata format) → panel row; null for malformed input. */
export function mapGmailThread(thread) {
  const msgs = thread?.messages
  if (!thread?.id || !Array.isArray(msgs) || msgs.length === 0) return null
  const last = msgs[msgs.length - 1]
  const header = (name) => (last.payload?.headers || []).find((h) => h.name?.toLowerCase() === name)?.value || ''
  return {
    id: thread.id,
    subject: header('subject') || '(no subject)',
    from: header('from'),
    date: header('date'),
    snippet: last.snippet || '',
    messageCount: msgs.length,
    link: `https://mail.google.com/mail/u/0/#all/${thread.id}`,
  }
}

/**
 * The viewer's threads with this contact — fetched live, displayed, NEVER
 * stored (privacy contract: nobody sees anyone else's inbox). A failed
 * per-thread metadata get drops that thread, never the whole list.
 */
export async function gmailListThreads(email, { maxResults = 8 } = {}) {
  const q = encodeURIComponent(`from:${email} OR to:${email}`)
  const list = await googleFetch(`${GMAIL_BASE}/threads?q=${q}&maxResults=${maxResults}`)
  const stubs = list?.threads || []
  const full = await Promise.all(stubs.map((t) =>
    googleFetch(`${GMAIL_BASE}/threads/${t.id}?format=metadata&metadataHeaders=Subject&metadataHeaders=From&metadataHeaders=Date`)
      .catch(() => null)))
  const mapped = full.map(mapGmailThread).filter(Boolean)
  if (stubs.length > 0 && mapped.length === 0) {
    // The list said threads exist but every detail fetch failed — that is an
    // error state, not "no email history"; surfacing it prevents the empty
    // state from masking auth/scope/network problems.
    throw new GoogleApiError(0, 'Could not load email threads — try again')
  }
  return mapped
}

/**
 * Pure: a Gmail web compose URL prefilled with the message — the "Open in
 * Gmail" alternative to sending from the hub. encodeURIComponent (spaces as
 * %20, never `+`, which Gmail would show literally). Empty fields are omitted.
 */
export function gmailComposeUrl({ to, cc, bcc, subject, body }) {
  const parts = [
    ['to', normalizeAddressList(to).join(',')],
    ['cc', normalizeAddressList(cc).join(',')],
    ['bcc', normalizeAddressList(bcc).join(',')],
    ['su', subject || ''],
    ['body', body || ''],
  ]
  const qs = parts.filter(([, v]) => v).map(([k, v]) => `&${k}=${encodeURIComponent(v)}`).join('')
  return `https://mail.google.com/mail/?view=cm&fs=1${qs}`
}

/** Send a plain-text email as the current user (optional cc/bcc). Returns {id, threadId}. */
export async function gmailSend({ to, cc, bcc, subject, body }) {
  const raw = toBase64Url(buildMimeMessage({ to, cc, bcc, subject, body }))
  return googleFetch(`${GMAIL_BASE}/messages/send`, { method: 'POST', body: { raw } })
}
